import { DashboardPeriodEcho } from './dashboard-filters.model';
import { DashboardVm } from './dashboard-vm.model';

/** The seven funnel bars, in wire order. Bar N counts every cohort row at rank N or beyond. */
export enum PipelineFunnelStage {
  STARTED = 'started',
  SUBMITTED = 'submitted',
  PROCESSING = 'processing',
  APPROVED = 'approved',
  CLOSING = 'closing',
  OPEN = 'open',
  FUNDED = 'funded',
}

export interface FunnelBar {
  stage: PipelineFunnelStage;
  count: number;
  /** Null only when the cohort is empty (`started` = 0); an empty bar inside a real cohort carries 0. */
  percentOfStarted: number | null;
  /** Non-null only on `approved`; null elsewhere means "no badge", never "zero aging". */
  agingCount: number | null;
}

export interface PipelineRatio {
  /** Null when `denominator` is 0. */
  percent: number | null;
  numerator: number;
  denominator: number;
}

export interface PipelineCards {
  blockedOnIncomplete: PipelineRatio;
  approvedVsSubmitted: PipelineRatio;
  fundedVsApproved: PipelineRatio;
}

export interface PipelineHealth {
  asOfUtc: Date;
  period: DashboardPeriodEcho;
  /** Always seven entries, always in `PipelineFunnelStage` order. */
  funnel: FunnelBar[];
  /** Server-owned badge threshold in days; the chip and the legend both follow it. */
  agingThresholdDays: number;
  cards: PipelineCards;
}

export interface FunnelBarVm {
  stage: PipelineFunnelStage;
  label: string;
  count: number;
  /** Whole percent, or `'—'` when the cohort is empty. */
  percentLabel: string;
  /** Track width; 0 for an empty bar, floored at `MIN_BAR_WIDTH_PERCENT` once the bar has rows. */
  widthPercent: number;
  /** Null on every bar without a badge, and on `approved` when nothing is aging. */
  agingLabel: string | null;
}

/**
 * State of a card's value and its progress indicator. There are three verdicts; `NO_VALUE` (null percent)
 * and `UNBENCHMARKED` (no peer median) are the absence of one and must never be painted like a result.
 * They stay separate: one shared "neutral" would let a later change quietly render a real number grey.
 */
export enum PipelineCardTier {
  GOOD = 'good',
  WARNING = 'warning',
  DANGER = 'danger',
  NO_VALUE = 'no-value',
  UNBENCHMARKED = 'unbenchmarked',
}

export interface PipelineCardVm {
  title: string;
  percentLabel: string;
  countLabel: string;
  /** Value for the mini progress bar; 0 when the ratio has no denominator. */
  progressPercent: number;
  tier: PipelineCardTier;
}

export interface PipelineHealthDataVm {
  bars: FunnelBarVm[];
  cards: PipelineCardVm[];
  agingThresholdDays: number;
  /** Legend pill copy, carrying the server's threshold. */
  legendLabel: string;
}

export type PipelineHealthVm = DashboardVm<PipelineHealthDataVm>;
